
  import { Vue, Component, Prop } from "vue-property-decorator";

  import GET_CONTRACTOR_ATTACHMENTS from "@/apollo/contractors/GetContractorAttachments.gql";

  import ModalFullScreen from "@/components/modal/ModalFullScreen.vue";
  import UiButton from "@/components/button/ui-button.vue";
  import RouteNames from "@/entities/RouteNames";
  import { Project } from "@/generated/graphql";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
  import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
  import RemoveListItem from "@/components/common/RemoveListItem.vue";
  import { NOTIFICATION } from "@/entities/Events";
  import Message from "@/data/notificationsMessages";
  import DELETE_CONTRACTOR_DATA from "@/apollo/contractors/DeleteContractorData.gql";

  interface ContractorProject {
    id: string;
    name: string;
    activitiesCount: number;
  }

  interface ContractorUser {
    id: string;
    given_name: string;
    family_name: string;
  }

  interface ContractorData {
    id: string;
    user: ContractorUser;
    projects: ContractorProject[];
    managersCount: number;
    teamsCount: number;
    documentsCount: number;
  }

  @Component({
    components: {
      RemoveListItem,
      ModalSectionsDivider,
      LoadingOverlay,
      ModalFullScreen,
      UiButton,
    },
    apollo: {
      contractorData: {
        query: GET_CONTRACTOR_ATTACHMENTS,
        variables() {
          return {
            contractorId: this.contractorId,
          };
        },
        update(data): ContractorData {
          const {
            contractor_projects,
            contractor
          } = data;

          const {
            id,
            contractorUser,
            teams_aggregate,
            managers_aggregate,
          } = contractor;

          const {
            attached_documents_aggregate,
            ...user
          } = contractorUser;

          const projects: ContractorProject[] = contractor_projects.map(({
             id,
             name,
             project_activities_aggregate,
           }: Partial<Project>) => ({
            id,
            name,
            activitiesCount: project_activities_aggregate!.aggregate!.count,
          }))

          return {
            id,
            user,
            projects,
            managersCount: managers_aggregate.aggregate.count,
            teamsCount: teams_aggregate.aggregate.count,
            documentsCount: attached_documents_aggregate.aggregate.count,
          }
        },
      },
    }
  })
  export default class RemoveFromCompanyModal extends Vue {
    @Prop({ type: String, required: true }) readonly contractorId!: string;
    @Prop({ type: String, required: true }) readonly companyId!: string;

    contractorData: ContractorData | null = null;
    routeNames = RouteNames;
    processing: boolean = false;

    get contractorFullName(): string {
      const {
        given_name: givenName,
        family_name: familyName,
      } = this.contractorData!.user;
      return `${givenName} ${familyName}`;
    }

    get isContractorDataLoaded(): boolean {
      return this.contractorData !== null;
    }

    get isLoading(): boolean {
      return this.$apollo.queries.contractorData.loading;
    }

    get isContractorDataEmpty(): boolean {
      const dataExist = [
        this.isProjectsExist,
        this.isManagersHasAccess,
        this.isTeamMember,
        this.isDocumentsAttached,
        ].includes(true);

      return !dataExist;
    }

    get isProjectsExist(): boolean {
      if (!this.contractorData) {
        return false;
      }

      return this.contractorData.projects.length > 0;
    }

    get isManagersHasAccess(): boolean {
      if (!this.contractorData) {
        return false;
      }

      return this.contractorData.managersCount > 0;
    }

    get isTeamMember(): boolean {
      if (!this.contractorData) {
        return false;
      }

      return this.contractorData.teamsCount > 0;
    }

    get isDocumentsAttached(): boolean {
      if (!this.contractorData) {
        return false;
      }

      return this.contractorData.documentsCount > 0;
    }

    get projectsListTitle(): string {
      return `Projects with uncompleted tasks - ${this.contractorData!.projects.length}:`;
    }

    getProjectContent(project: ContractorProject): string {
      const { name, activitiesCount } = project;
      const activityText = activitiesCount === 1 ? "task" : "tasks";

      return `${name} project - ${activitiesCount} ${activityText}`;
    }

    async submit() {
      if (this.isLoading) {
        return;
      }

      this.processing = true;

      try {
        await this.removeContractorFromCompanyDB();

        this.$router.push({
          name: RouteNames.COMPANY_DATABASE,
        });

      } catch (_) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage("Something went wrong!"));
      } finally {
        this.processing = false;
      }
    }

    closeModal(): void {
      if (this.processing) {
        return;
      }

      this.$emit('close');
    }

    private removeContractorFromCompanyDB() {
      return this.$apollo.mutate({
        mutation: DELETE_CONTRACTOR_DATA,
        variables: {
          pk_columns: { id: this.contractorId },
          _set: { deleted: true, approved: false },
          contractorUserId: this.contractorData!.user.id,
          companyId: this.companyId,
        },
      });
    }
  }
